import React, { useEffect, useMemo, useState } from 'react';
import { listUpcomingEventsPage, listPastEventsPage } from '../../utils/events';
import { Link, useLocation } from 'react-router-dom';
import SEO from '../seo/SEO';

function useQuery() {
	const { search } = useLocation();
	return useMemo(() => new URLSearchParams(search), [search]);
}

const EventRow = ({ ev }) => {
	const start = ev.startAt?.toDate ? ev.startAt.toDate() : new Date(ev.startAt);
	return (
		<tr className="border-b">
			<td className="py-3 pr-4 align-top whitespace-nowrap text-sm text-gray-700">
				{start.toLocaleDateString()} {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
			</td>
			<td className="py-3 pr-4 align-top">
				<Link to={`/events/${ev.id}`} className="text-[rgb(130,88,18)] font-semibold hover:underline">{ev.title}</Link>
				<p className="text-sm text-gray-600 line-clamp-2">{ev.description}</p>
			</td>
			<td className="py-3 align-top text-sm text-gray-700">{ev.isOnline ? 'Online' : ev.location}</td>
		</tr>
	);
};

function EventsPage() {
	const q = useQuery();
	const [tab, setTab] = useState(q.get('tab') === 'past' ? 'past' : 'upcoming');
    const [upcoming, setUpcoming] = useState([]);
    const [past, setPast] = useState([]);
    const [loading, setLoading] = useState(true);
    const [upCursor, setUpCursor] = useState(null);
    const [pastCursor, setPastCursor] = useState(null);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const [{ items: upItems, nextCursor: upNext }, { items: pastItems, nextCursor: pastNext }] = await Promise.all([
                    listUpcomingEventsPage({ pageSize: 12 }),
                    listPastEventsPage({ pageSize: 12 })
                ]);
                if (!mounted) return;
                setUpcoming(upItems);
                setPast(pastItems);
                setUpCursor(upNext);
                setPastCursor(pastNext);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, []);

    const loadMoreUpcoming = async () => {
        if (!upCursor) return;
        const { items, nextCursor } = await listUpcomingEventsPage({ pageSize: 12, cursor: upCursor });
        setUpcoming(prev => [...prev, ...items]);
        setUpCursor(nextCursor);
    };

    const loadMorePast = async () => {
        if (!pastCursor) return;
        const { items, nextCursor } = await listPastEventsPage({ pageSize: 12, cursor: pastCursor });
        setPast(prev => [...prev, ...items]);
        setPastCursor(nextCursor);
    };

	return (
		<>
			<SEO 
				title="Events - Intellectual Oasis Fellowship"
				description="Explore upcoming and past events from Intellectual Oasis Fellowship. Join webinars, workshops, and community gatherings focused on educational excellence and academic development."
				pathname="/events"
				image="/images/events-og.jpg"
			/>
			<div className="max-w-5xl mx-auto">
				<div className="mb-6">
					<h1 className="text-3xl font-extrabold text-gray-900">Events</h1>
					<p className="text-gray-600">Join our upcoming events or explore highlights from past ones.</p>
				</div>
				<div className="mb-6 border-b">
					<nav className="flex gap-6" aria-label="Tabs">
						<button 
							onClick={() => setTab('upcoming')}
							role="tab"
							aria-selected={tab === 'upcoming'}
							aria-controls="upcoming-tab"
							className={`py-2 border-b-2 -mb-px ${tab==='upcoming' ? 'border-[rgb(130,88,18)] text-[rgb(130,88,18)]' : 'border-transparent text-gray-600'}`}
						>
							Upcoming
						</button>
						<button 
							onClick={() => setTab('past')}
							role="tab"
							aria-selected={tab === 'past'}
							aria-controls="past-tab"
							className={`py-2 border-b-2 -mb-px ${tab==='past' ? 'border-[rgb(130,88,18)] text-[rgb(130,88,18)]' : 'border-transparent text-gray-600'}`}
						>
							Past
						</button>
					</nav>
				</div>
			{loading ? (
				<p className="text-gray-600">Loading...</p>
            ) : (
                <table className="w-full">
					<thead>
						<tr className="text-left text-sm text-gray-500">
							<th className="font-medium pb-2 pr-4">Date</th>
							<th className="font-medium pb-2 pr-4">Event</th>
							<th className="font-medium pb-2">Where</th>
						</tr>
					</thead>
					<tbody>
						{(tab === 'upcoming' ? upcoming : past).map(ev => (
							<EventRow key={ev.id} ev={ev} />
						))}
					</tbody>
                </table>
            )}
            {tab === 'upcoming' && upCursor && (
                <div className="mt-6 text-center">
                    <button onClick={loadMoreUpcoming} className="px-4 py-2 bg-[rgb(130,88,18)] text-white rounded-md hover:opacity-90">Load more</button>
                </div>
            )}
            {tab === 'past' && pastCursor && (
                <div className="mt-6 text-center">
                    <button onClick={loadMorePast} className="px-4 py-2 bg-[rgb(130,88,18)] text-white rounded-md hover:opacity-90">Load more</button>
                </div>
            )}
			</div>
		</>
	);
}

export default EventsPage;
